/* ---------------------------------------------------------------------------
 * Marketing CLIENT i18n resources — eager English, lazy active locale.
 *
 * IMPORTANT: this module is for the BROWSER bundle only. The SSR/prerender path
 * uses `resources.ts` (`buildI18nResources`) which eager-globs every locale at
 * build time in Node — that is fine because SSR output is not shipped to the
 * browser. Keeping the two paths in separate modules ensures the eager glob in
 * `resources.ts` never leaks into the client chunk.
 *
 * Client behaviour:
 *   - English (default route + i18next fallbackLng) is bundled eagerly so the
 *     `/` route renders instantly with no network round-trip.
 *   - Each non-English locale is code-split via `import.meta.glob` and loaded on
 *     demand. `useSuspense:false` means a non-en route shows English until its
 *     locale chunk resolves (sub-second flash on first paint; SSR already
 *     delivered the correct-language HTML so crawlers/first-paint are unaffected).
 * --------------------------------------------------------------------------*/
import type { i18n as I18n } from 'i18next';
import { isSupportedLocale, DEFAULT_LOCALE, type SupportedLocale } from '@noxverify/i18n';

import enCommon from '../../../packages/i18n/locales/en/common.json';
import enSeo from '../../../packages/i18n/locales/en/seo.json';
import enMarketing from '../../../packages/i18n/locales/en/marketing.json';
import enLegal from '../../../packages/i18n/locales/en/legal.json';
import enBlog from '../../../packages/i18n/locales/en/blog.json';

const NS = ['common', 'seo', 'marketing', 'legal', 'blog'] as const;

/** Eager English resources used to initialise i18next synchronously. */
export const eagerEnResources = {
  en: {
    common: enCommon as Record<string, unknown>,
    seo: enSeo as Record<string, unknown>,
    marketing: enMarketing as Record<string, unknown>,
    legal: enLegal as Record<string, unknown>,
    blog: enBlog as Record<string, unknown>,
  },
};

// Lazy locale modules per namespace; Vite emits one chunk per imported module.
// English is bundled eagerly above, so each glob EXCLUDES `en/*.json` via Vite's
// negative-pattern array. Without the exclusion the en JSON is both statically
// imported and dynamically globbed, which Vite warns cannot be code-split.
const lazyCommon = import.meta.glob<{ default: Record<string, unknown> }>([
  '../../../packages/i18n/locales/*/common.json',
  '!../../../packages/i18n/locales/en/common.json',
]);
const lazySeo = import.meta.glob<{ default: Record<string, unknown> }>([
  '../../../packages/i18n/locales/*/seo.json',
  '!../../../packages/i18n/locales/en/seo.json',
]);
const lazyMarketing = import.meta.glob<{ default: Record<string, unknown> }>([
  '../../../packages/i18n/locales/*/marketing.json',
  '!../../../packages/i18n/locales/en/marketing.json',
]);
const lazyLegal = import.meta.glob<{ default: Record<string, unknown> }>([
  '../../../packages/i18n/locales/*/legal.json',
  '!../../../packages/i18n/locales/en/legal.json',
]);
// Blog text. Only the priority locales (de/fr/es/ja/zh-Hans) ship a `blog.json`;
// the 16 fallback locales intentionally have NO blog.json so i18next falls back
// to the eager English bundle for the whole namespace (no `[lng]` stubs). The
// glob silently matches only the files that exist — `loaderFor` returns
// undefined for a locale without a blog bundle and English is used.
const lazyBlog = import.meta.glob<{ default: Record<string, unknown> }>([
  '../../../packages/i18n/locales/*/blog.json',
  '!../../../packages/i18n/locales/en/blog.json',
]);

const NS_MODULES: Record<
  (typeof NS)[number],
  Record<string, () => Promise<{ default: Record<string, unknown> }>>
> = {
  common: lazyCommon,
  seo: lazySeo,
  marketing: lazyMarketing,
  legal: lazyLegal,
  blog: lazyBlog,
};

function loaderFor(
  modules: Record<string, () => Promise<{ default: Record<string, unknown> }>>,
  locale: string,
): (() => Promise<{ default: Record<string, unknown> }>) | undefined {
  const suffix = `/locales/${locale}/`;
  for (const [path, load] of Object.entries(modules)) {
    if (path.includes(suffix)) return load;
  }
  return undefined;
}

const loaded = new Set<string>([DEFAULT_LOCALE]);

/** Add all marketing namespace bundles for `locale`, then re-render dependents. */
export async function ensureLocaleLoaded(i18n: I18n, locale: string): Promise<void> {
  if (loaded.has(locale) || !isSupportedLocale(locale)) return;
  loaded.add(locale);

  await Promise.all(
    NS.map(async (ns) => {
      const mod = await loaderFor(NS_MODULES[ns], locale)?.();
      if (mod) i18n.addResourceBundle(locale, ns, mod.default, true, true);
    }),
  );

  if (i18n.resolvedLanguage === locale || i18n.language === locale) {
    i18n.emit('languageChanged', locale);
  }
}

/** Load the active locale now and on every `languageChanged`. */
export function attachLazyLocaleLoader(i18n: I18n): () => void {
  const onChange = (lng: string) => {
    void ensureLocaleLoaded(i18n, lng);
  };
  i18n.on('languageChanged', onChange);
  const current = (i18n.resolvedLanguage ?? i18n.language) as SupportedLocale | undefined;
  if (current) void ensureLocaleLoaded(i18n, current);
  return () => i18n.off('languageChanged', onChange);
}
